/**
 * Frontend wrapper around the /qa-automation/* backend endpoints.
 * See plans/qa-automation-tenant-and-admin-page.md.
 */

import { apiRequest } from './api';

// ─── Types mirrored from QaAutomationService (server) ─────────────────────

export interface GenerateSummary {
  created: { flows: number; configs: number };
  updated: { flows: number; configs: number };
  unchanged: { flows: number; configs: number };
  removed: { configs: number };
}

export interface GenerateResponse {
  created:   { flows: string[]; configs: string[] };
  updated:   { flows: string[]; configs: string[] };
  unchanged: { flows: string[]; configs: string[] };
  removed:   { configs: string[] };
  summary:   GenerateSummary;
}

export interface RunAllResponse {
  batchId: string;
  runs: { primitiveId: string; cell: string; testRunId: string; testConfigId: string }[];
}

export interface QaCellStatus {
  name: string;
  testConfigId: string;
  allocationCapabilities: string[];
  forcedRat: string | null;
  forcedNetworkMccMnc: string | null;
  lastRunAt: string | null;
  lastRunStatus: 'completed' | 'failed' | 'cancelled' | 'running' | 'pending' | 'never';
  lastRunId: string | null;
  // Pointer into executions/{id} (what useExecutionLogs subscribes to). Different
  // collection than test_runs — the LogsModal would otherwise 404.
  lastExecutionId: string | null;
  lastBatchId: string | null;
  lastRunDurationMs: number | null;
  lastRunError: string | null;
  runsLast24h: { pass: number; fail: number };
  runsLast7d:  { pass: number; fail: number };
}

export interface QaPrimitiveStatus {
  primitiveId: string;
  primitiveName: string;
  catalogueVisibility: 'catalogue' | 'internal' | 'e2e' | 'unknown';
  flowId: string;
  flowName: string;
  assertionNodeCount: number;
  hasSentinelOnly: boolean;
  cells: QaCellStatus[];
}

export interface QaBatch {
  batchId: string;
  startedAt: string;
  total: number;
  pass: number;
  fail: number;
  running: number;
}

export interface QaStatus {
  primitives: QaPrimitiveStatus[];
  batches: QaBatch[];
  coverage: {
    primitivesTotal: number;
    primitivesAuthored: number;
    primitivesSentinelOnly: number;
    cellsTotal: number;
    cellsPassLast24h: number;
    cellsFailLast24h: number;
    cellsNoRunLast24h: number;
  };
}

// ─── Calls ─────────────────────────────────────────────────────────────────

/**
 * All three calls require an org context. Backend's `authenticate` resolves
 * `req.organizationId` from the user's Firestore record OR the
 * `X-Organization-Id` header. Super-admins don't have an auto-resolved org,
 * so we ALWAYS pass the active org id from the AuthContext to keep both
 * paths working.
 */
export async function generateTestFlows(organizationId: string): Promise<GenerateResponse> {
  return apiRequest('/qa-automation/generate-test-flows', { method: 'POST' }, organizationId);
}

export async function runAll(organizationId: string): Promise<RunAllResponse> {
  return apiRequest('/qa-automation/run-all', { method: 'POST' }, organizationId);
}

export async function getStatus(organizationId: string): Promise<QaStatus> {
  return apiRequest('/qa-automation/status', { method: 'GET' }, organizationId);
}
